import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Gift, Lock, Check, Play, Crown, Sparkles } from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";

const BASE_REWARDS = [0.5, 1.0, 1.5, 2.0, 3.0, 5.0, 10.0];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface StreakData {
  currentDay: number; // 0-6 index
  lastClaimDate: string; // ISO date string
  cycleCount: number; // how many 7-day cycles completed
  claimedToday: boolean;
}

function getStreakData(userId: string): StreakData {
  const key = `dailyStreak_${userId}`;
  const raw = localStorage.getItem(key);
  const today = new Date().toISOString().split("T")[0];

  if (raw) {
    const data: StreakData = JSON.parse(raw);
    const lastDate = data.lastClaimDate;

    // Check if streak is broken (more than 1 day gap)
    const lastDateObj = new Date(lastDate);
    const todayObj = new Date(today);
    const diffDays = Math.floor((todayObj.getTime() - lastDateObj.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays > 1) {
      // Streak broken — reset
      return { currentDay: 0, lastClaimDate: "", cycleCount: data.cycleCount, claimedToday: false };
    }

    if (lastDate === today) {
      return { ...data, claimedToday: true };
    }

    return { ...data, claimedToday: false };
  }

  return { currentDay: 0, lastClaimDate: "", cycleCount: 0, claimedToday: false };
}

function saveStreakData(userId: string, data: StreakData) {
  const key = `dailyStreak_${userId}`;
  localStorage.setItem(key, JSON.stringify(data));
}

const DailyLoginReward: React.FC = () => {
  const theme = useThemeHook();
  const isDark = theme === "dark";
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const userId = userData?.user?.id;

  const [streakData, setStreakData] = useState<StreakData>({
    currentDay: 0,
    lastClaimDate: "",
    cycleCount: 0,
    claimedToday: false,
  });
  const [loading, setLoading] = useState(false);
  const [justClaimed, setJustClaimed] = useState(false);

  useEffect(() => {
    if (userId) {
      setStreakData(getStreakData(userId));
    }
  }, [userId]);

  const multiplier = 1 + streakData.cycleCount * 0.2; // 20% increase per cycle
  const rewards = BASE_REWARDS.map((r) => +(r * multiplier).toFixed(1));
  const currentDay = streakData.currentDay;
  const canClaim = !streakData.claimedToday;
  const requiresAd = currentDay > 0; // Day 1 (index 0) is free

  const handleClaim = async () => {
    if (!canClaim || loading) return;

    const doClaim = () => {
      const today = new Date().toISOString().split("T")[0];
      const nextDay = currentDay + 1;
      const newData: StreakData = {
        currentDay: nextDay >= 7 ? 0 : nextDay,
        lastClaimDate: today,
        cycleCount: nextDay >= 7 ? streakData.cycleCount + 1 : streakData.cycleCount,
        claimedToday: true,
      };
      saveStreakData(userId, newData);
      setStreakData(newData);
      setJustClaimed(true);
      setTimeout(() => setJustClaimed(false), 3000);
    };

    if (requiresAd) {
      setLoading(true);
      try {
        await piNetworkAdsManager.handleRewardedAdFlowFront(userId, () => {
          doClaim();
        });
      } catch {
        // Ad failed
      } finally {
        setLoading(false);
      }
    } else {
      doClaim();
    }
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${
      isDark ? "bg-slate-800/50 border-slate-700/50" : "bg-white border-gray-200 shadow-sm"
    }`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        isDark ? "bg-indigo-900/30" : "bg-indigo-50"
      }`}>
        <div className="flex items-center gap-2">
          <Crown size={16} className="text-indigo-500" />
          <span className={`font-black text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
            Daily Streak
          </span>
          {streakData.cycleCount > 0 && (
            <span className="text-[9px] font-bold bg-indigo-500 text-white px-1.5 py-0.5 rounded-full">
              Cycle {streakData.cycleCount + 1}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Sparkles size={12} className="text-amber-500" />
          <span className={`text-[10px] font-bold ${isDark ? "text-amber-300" : "text-amber-600"}`}>
            Day {currentDay + 1}/7
          </span>
        </div>
      </div>

      {/* Days row */}
      <div className="px-3 py-3">
        <div className="flex gap-1.5">
          {rewards.map((reward, idx) => {
            const isCompleted = idx < currentDay || (idx === currentDay && streakData.claimedToday);
            const isCurrent = idx === currentDay && !streakData.claimedToday;
            const isLocked = idx > currentDay;
            const isDay7 = idx === 6;

            return (
              <motion.div
                key={idx}
                initial={isCurrent ? { scale: 0.9 } : {}}
                animate={isCurrent ? { scale: [0.95, 1.05, 0.95] } : {}}
                transition={isCurrent ? { repeat: Infinity, duration: 2 } : {}}
                className={`flex-1 rounded-xl p-1.5 text-center relative overflow-hidden ${
                  isCompleted
                    ? "bg-emerald-100 border border-emerald-200"
                    : isCurrent
                    ? isDay7
                      ? "bg-gradient-to-b from-amber-100 to-yellow-100 border-2 border-amber-400 shadow-md shadow-amber-200/50"
                      : "bg-gradient-to-b from-indigo-100 to-purple-100 border-2 border-indigo-400 shadow-md shadow-indigo-200/50"
                    : isDark
                    ? "bg-slate-700/50 border border-slate-600/50"
                    : "bg-gray-50 border border-gray-200"
                }`}
              >
                <div className={`text-[8px] font-bold mb-0.5 ${
                  isCompleted ? "text-emerald-600" : isCurrent ? "text-indigo-600" : isDark ? "text-slate-500" : "text-gray-400"
                }`}>
                  {DAY_LABELS[idx]}
                </div>

                <div className="flex justify-center mb-0.5">
                  {isCompleted ? (
                    <Check size={14} className="text-emerald-500" />
                  ) : isCurrent ? (
                    <Gift size={14} className={isDay7 ? "text-amber-500" : "text-indigo-500"} />
                  ) : (
                    <Lock size={12} className={isDark ? "text-slate-600" : "text-gray-300"} />
                  )}
                </div>

                <div className={`text-[10px] font-black ${
                  isCompleted ? "text-emerald-700" : isCurrent
                    ? isDay7 ? "text-amber-700" : "text-indigo-700"
                    : isDark ? "text-slate-500" : "text-gray-400"
                }`}>
                  +{reward}
                </div>

                {idx > 0 && !isCompleted && (
                  <div className="flex justify-center mt-0.5">
                    <Play size={7} className={isDark ? "text-slate-600" : "text-gray-300"} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Claim button */}
      <div className="px-3 pb-3">
        {justClaimed ? (
          <div className="w-full py-3 rounded-xl bg-emerald-500 text-white text-center font-black text-sm">
            ✅ +{rewards[currentDay > 0 ? currentDay - 1 : 0]} ATH Claimed!
          </div>
        ) : canClaim ? (
          <button
            onClick={handleClaim}
            disabled={loading}
            className={`w-full py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-95 ${
              currentDay === 6
                ? "bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-lg shadow-amber-500/30"
                : "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/30"
            } disabled:opacity-50`}
          >
            {loading ? (
              <span className="animate-pulse">Loading Ad...</span>
            ) : (
              <>
                {requiresAd && <Play size={14} fill="white" />}
                {currentDay === 0 ? "Claim Free Reward" : `Watch Ad & Claim +${rewards[currentDay]} ATH`}
              </>
            )}
          </button>
        ) : (
          <div className={`w-full py-3 rounded-xl text-center text-sm font-bold ${
            isDark ? "bg-slate-700 text-slate-500" : "bg-gray-100 text-gray-400"
          }`}>
            Come back tomorrow for Day {currentDay + 1}! 🔥
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyLoginReward;
